import { yupResolver } from '@hookform/resolvers/yup';
import { addDays, addMinutes, format, isWeekend } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import isWithinTimeRange from '@medflex/utils/isWithinTimeRange';
import MaskedTextInput from 'react-text-mask';
import createAutoCorrectedDatePipe from 'text-mask-addons/dist/createAutoCorrectedDatePipe';
import useClassNames from '@medflex/hooks/useClassNames';
import Button from '../../Button';
import InputField from '../../InputField';
import SessionsFormField from '../SessionsFormField';
import useSessionsScheduleSchema from './useSessionsScheduleSchema';
import InputCheckbox from '../../InputCheckbox';

const SessionsScheduleForm = ({
  onSubmit,
  isEditMode,
  isCopyMode,
  maxParticipants,
  isDisabled,
  defaultTitle,
  defaultDate,
  defaultTime,
  defaultAnonymous,
  participantName,
  now,
  onClose,
  children,
  buttonText,
  nowButtonHidden,
  todayButtonHidden,
  minPastMinutesPerDayToStart,
  maxPastMinutesPerDayToStart,
  onlyWeekDays,
  notSameDay,
  titleFieldHidden,
  className,
  ...props
}) => {
  const { schema: validationSchema } = useSessionsScheduleSchema({
    minPastMinutesPerDayToStart,
    maxPastMinutesPerDayToStart,
    onlyWeekDays,
    notSameDay,
    titleFieldHidden,
    isTitleRequired: maxParticipants > 2,
  });
  const cn = useClassNames(() => ['rounded', 'p-2', 'lg:p-4', 'mb-6', className], [className]);
  const {
    register,
    handleSubmit: wrapSubmit,
    errors,
    setValue,
    control,
    watch,
  } = useForm({
    resolver: yupResolver(validationSchema),
  });
  const { t } = useTranslation('components', { keyPrefix: 'Sessions.SessionsScheduleForm' });
  const dateInputRef = useRef();
  const [anonymous, setAnonymous] = useState(defaultAnonymous);

  const quarterHours = useMemo(() => {
    if (now) {
      const quarter = 15 * 60 * 1000;
      const nearestQuarter = new Date(Math.round(now() / quarter) * quarter);
      const nextQuarter =
        nearestQuarter.getTime() <= now().getTime()
          ? addMinutes(nearestQuarter, 15)
          : nearestQuarter;
      return [
        addMinutes(nextQuarter, 0),
        addMinutes(nextQuarter, 15),
        addMinutes(nextQuarter, 30),
        addMinutes(nextQuarter, 45),
      ];
    }
    return null;
  }, [now]);

  const { todayIsWeekDay, tomorrowIsWeekDay, nowIsWithinTimeRange } = useMemo(
    () => ({
      todayIsWeekDay: !isWeekend(now()),
      tomorrowIsWeekDay: !isWeekend(addDays(now(), 1)),
      nowIsWithinTimeRange: isWithinTimeRange({
        timeString: format(now(), 'HH:mm'),
        minPastMinutesPerDay: minPastMinutesPerDayToStart,
        maxPastMinutesPerDay: maxPastMinutesPerDayToStart,
      }),
    }),
    [],
  );

  const handleSubmit = (data, event) => {
    const { title, ...rest } = data;
    onSubmit(
      {
        ...rest,
        title:
          title ||
          t('title.singleSessionDefaultTitle', {
            name: participantName,
          }),
      },
      event,
    );
  };

  useEffect(() => {
    if (isCopyMode) {
      dateInputRef.current.focus();
    }
  }, [dateInputRef.current]);

  useEffect(() => {
    setAnonymous(defaultAnonymous);
  }, [defaultAnonymous]);

  return (
    <form
      data-component="SessionsScheduleForm"
      method="post"
      onSubmit={wrapSubmit(handleSubmit)}
      {...props}
    >
      <div className={cn}>
        {!titleFieldHidden && (
          <div className="mb-4">
            <SessionsFormField label={t('title.label')} error={errors.title}>
              <InputField
                name="title"
                ref={register}
                placeholder={
                  maxParticipants === 2 && participantName
                    ? t('title.singleSessionDefaultTitle', {
                        name: participantName,
                      })
                    : ''
                }
                defaultValue={defaultTitle}
                state={errors.title ? 'invalid' : 'default'}
              />
            </SessionsFormField>
          </div>
        )}
        <div className="flex flex-wrap">
          <SessionsFormField className="mb-2 flex-grow" label={t('date.label')} error={errors.date}>
            <MaskedTextInput
              name="date"
              placeholder={t('date.placeholder')}
              guide={false}
              keepCharPositions
              pipe={createAutoCorrectedDatePipe('dd.mm.yyyy')}
              mask={[/\d/, /\d/, '.', /\d/, /\d/, '.', /\d/, /\d/, /\d/, /\d/]}
              defaultValue={defaultDate}
              render={(ref, _props) => (
                <InputField
                  {..._props}
                  state={errors.date ? 'invalid' : 'default'}
                  ref={node => {
                    register(node);
                    ref(node);
                    dateInputRef.current = node;
                  }}
                />
              )}
            />
            {!nowButtonHidden &&
              ((!onlyWeekDays && !minPastMinutesPerDayToStart) ||
                (todayIsWeekDay && nowIsWithinTimeRange)) && (
                <button
                  type="button"
                  className="text-blue-500 hover:underline cursor-pointer mr-4 focus:outline-none"
                  onClick={() => {
                    const date = now();
                    setValue('date', format(date, 'DD.MM.YYYY'), { shouldValidate: true });
                    setValue('time', format(addMinutes(date, onlyWeekDays ? 7 : 2), 'HH:mm'), {
                      shouldValidate: true,
                    });
                  }}
                >
                  {t('date.now')}
                </button>
              )}
            {(!onlyWeekDays || todayIsWeekDay) && !todayButtonHidden && (
              <button
                type="button"
                className="text-blue-500 hover:underline cursor-pointer mr-4 focus:outline-none"
                onClick={() =>
                  setValue('date', format(now(), 'DD.MM.YYYY'), { shouldValidate: true })
                }
              >
                {t('date.today')}
              </button>
            )}
            {(!onlyWeekDays || tomorrowIsWeekDay) && (
              <button
                type="button"
                className="text-blue-500 hover:underline cursor-pointer focus:outline-none"
                onClick={() =>
                  setValue('date', format(addDays(now(), 1), 'DD.MM.YYYY'), {
                    shouldValidate: true,
                  })
                }
              >
                {t('date.tomorrow')}
              </button>
            )}
          </SessionsFormField>
          <div className="w-full lg:w-4 mb-2 lg:mb-0" />
          <SessionsFormField className="flex-grow" label={t('time.label')} error={errors.time}>
            <MaskedTextInput
              name="time"
              placeholder={t('time.placeholder')}
              guide={false}
              keepCharPositions
              pipe={createAutoCorrectedDatePipe('HH:MM')}
              mask={[/\d/, /\d/, ':', /\d/, /\d/]}
              defaultValue={defaultTime}
              render={(ref, _props) => (
                <InputField
                  {..._props}
                  state={errors.date ? 'invalid' : 'default'}
                  ref={node => {
                    register(node);
                    ref(node);
                  }}
                />
              )}
            />
            {(!minPastMinutesPerDayToStart || nowIsWithinTimeRange) &&
              quarterHours &&
              quarterHours.map(quarter => (
                <button
                  key={quarter.getTime()}
                  type="button"
                  onClick={() =>
                    setValue('time', format(quarter, 'HH:mm'), { shouldValidate: true })
                  }
                  className="text-blue-500 hover:underline cursor-pointer mr-4 focus:outline-none"
                >
                  {format(quarter, 'HH:mm')}
                </button>
              ))}
          </SessionsFormField>
        </div>
      </div>
      <h3 className="text-xl font-semibold mb-2">{t('options')}</h3>
      <div className="bg-slate-100 p-4 space-y-4 mb-6">
        {children &&
          children({
            register,
            errors,
            setValue,
            control,
            watch,
          })}
        {!isEditMode && (
          <div className="mb-6">
            <label className="flex" htmlFor="anonymous">
              <InputCheckbox
                id="anonymous"
                ref={register}
                name="anonymous"
                checked={anonymous}
                onChange={() => setAnonymous(!anonymous)}
              />
              <span className="ml-2">
                <Trans t={t} i18nKey="anonymous_invitation">
                  <b>send invitation anonymously</b> - name of the practitioner will not be shown in
                  the invitation message
                </Trans>
              </span>
            </label>
          </div>
        )}
      </div>
      <div className="flex w-full justify-end">
        {onClose && (
          <>
            <div className="flex-grow" />
            <Button kind="secondary:ghost" className="text-sm" onClick={onClose}>
              {t('close')}
            </Button>
          </>
        )}
        <Button
          type="submit"
          className={`${isEditMode && 'text-sm ml-4'} w-full lg:w-auto`}
          isDisabled={isDisabled}
        >
          {buttonText ||
            t(`submit.${isEditMode ? 'update' : 'create'}`, {
              count: maxParticipants - 1,
            })}
        </Button>
      </div>
    </form>
  );
};

SessionsScheduleForm.propTypes = {
  onSubmit: PropTypes.func,
  onClose: PropTypes.func,
  isDisabled: PropTypes.bool,
  now: PropTypes.func,
  defaultTitle: PropTypes.string,
  defaultDate: PropTypes.string,
  defaultTime: PropTypes.string,
  defaultAnonymous: PropTypes.bool,
  isEditMode: PropTypes.bool,
  isCopyMode: PropTypes.bool,
  maxParticipants: PropTypes.number,
  participantName: PropTypes.string,
  minPastMinutesPerDayToStart: PropTypes.number,
  maxPastMinutesPerDayToStart: PropTypes.number,
  buttonText: PropTypes.string,
  onlyWeekDays: PropTypes.bool,
  nowButtonHidden: PropTypes.bool,
  todayButtonHidden: PropTypes.bool,
  notSameDay: PropTypes.bool,
  titleFieldHidden: PropTypes.bool,
  children: PropTypes.func,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.array,
    PropTypes.object,
  ]),
};

SessionsScheduleForm.defaultProps = {
  onSubmit: () => {},
  isDisabled: false,
  todayButtonHidden: false,
  now: () => new Date(),
  defaultTitle: '',
  defaultDate: null,
  defaultTime: null,
  defaultAnonymous: false,
  children: () => {},
  isEditMode: false,
  isCopyMode: false,
  maxParticipants: 9,
};

export default SessionsScheduleForm;
